import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { buildApiUrl } from '../../config/api';

interface BackupSettings {
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  emailAddress: string;
  retentionCount: number;
  includeStatistics: boolean;
  compressionEnabled: boolean;
  fileFormat: 'json' | 'csv';
  lastBackupAt?: number;
  createdAt: number;
  updatedAt: number;
}

interface BackupStats {
  backupEnabled: boolean;
  frequency: string;
  totalBackups: number;
  successfulBackups: number;
  failedBackups: number;
  totalSize: number;
  lastBackup?: {
    timestamp: number;
    status: string;
    size: number;
    promptCount: number;
  };
  nextBackupDue?: number;
}

interface BackupHistoryEntry {
  id: string;
  userId: string;
  backupId: string;
  timestamp: number;
  backupType: 'auto' | 'manual';
  status: 'success' | 'failed' | 'in_progress';
  size: number;
  promptCount: number;
  emailSent: boolean;
  createdAt: number;
}

interface BackupSettingsPageProps {
  onNavigate?: (page: string) => void;
}

export const BackupSettingsPage: React.FC<BackupSettingsPageProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<BackupSettings | null>(null);
  const [stats, setStats] = useState<BackupStats | null>(null);
  const [history, setHistory] = useState<BackupHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [backupFormat, setBackupFormat] = useState<'json' | 'csv'>('csv');

  // 获取备份设置
  const fetchBackupSettings = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('未找到登录令牌，请重新登录');
      }

      const response = await fetch(buildApiUrl('/api/v1/me/backup/settings'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}: 获取备份设置失败`);
      }

      const data = await response.json();
      // 确保包含默认的fileFormat字段
      const settingsData = {
        ...data.data,
        fileFormat: data.data.fileFormat || 'csv'  // 默认为CSV格式
      };
      setSettings(settingsData);
    } catch (error: any) {
      console.error('获取备份设置失败:', error);
      setError(error.message || '获取备份设置失败');
    }
  };

  // 获取备份统计
  const fetchBackupStats = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('未找到登录令牌，请重新登录');
      }

      const response = await fetch(buildApiUrl('/api/v1/me/backup/stats'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}: 获取备份统计失败`);
      }

      const data = await response.json();
      setStats(data.data);
    } catch (error: any) {
      console.error('获取备份统计失败:', error);
      setError(error.message || '获取备份统计失败');
    }
  };

  // 获取备份历史
  const fetchBackupHistory = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('未找到登录令牌，请重新登录');
      }

      const response = await fetch(buildApiUrl('/api/v1/me/backup/history?limit=10'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}: 获取备份历史失败`);
      }

      const data = await response.json();
      setHistory(data.data.history);
    } catch (error: any) {
      console.error('获取备份历史失败:', error);
      setError(error.message || '获取备份历史失败');
    }
  };

  // 初始化数据
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchBackupSettings(),
        fetchBackupStats(),
        fetchBackupHistory(),
      ]);
      setLoading(false);
    };

    loadData();
  }, []);

  // 更新备份设置
  const updateSettings = async (updates: Partial<BackupSettings>) => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(buildApiUrl('/api/v1/me/backup/settings'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error('更新备份设置失败');
      }

      const data = await response.json();
      // 确保包含默认的fileFormat字段
      const settingsData = {
        ...data.data,
        fileFormat: data.data.fileFormat || 'csv'  // 默认为CSV格式
      };
      setSettings(settingsData);
      setSuccess('备份设置更新成功');
      
      // 刷新统计数据
      await fetchBackupStats();
    } catch (error: any) {
      setError(error.message || '更新备份设置失败');
    } finally {
      setSaving(false);
    }
  };

  // 触发手动备份（使用设置中的格式）
  const triggerManualBackup = async () => {
    setTriggering(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(buildApiUrl('/api/v1/me/backup/trigger'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('触发备份失败');
      }

      const data = await response.json();
      setSuccess(`手动备份成功！备份ID: ${data.data.backupId}`);
      
      // 刷新数据
      await Promise.all([
        fetchBackupStats(),
        fetchBackupHistory(),
      ]);
    } catch (error: any) {
      setError(error.message || '触发备份失败');
    } finally {
      setTriggering(false);
    }
  };

  // 触发带格式选择的手动备份
  const triggerManualBackupWithFormat = async (format: 'json' | 'csv') => {
    setTriggering(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(buildApiUrl('/api/v1/me/backup/trigger'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fileFormat: format }),
      });

      if (!response.ok) {
        throw new Error('触发备份失败');
      }

      const data = await response.json();
      setSuccess(`${format.toUpperCase()}格式备份邮件发送成功！备份ID: ${data.data.backupId}`);
      
      // 刷新数据
      await Promise.all([
        fetchBackupStats(),
        fetchBackupHistory(),
      ]);
    } catch (error: any) {
      setError(error.message || '触发备份失败');
    } finally {
      setTriggering(false);
    }
  };

  // 直接下载备份
  const downloadBackup = async (format: 'json' | 'csv' = 'json') => {
    setTriggering(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('未找到登录令牌，请重新登录');
      }

      // 获取用户信息
      const userResponse = await fetch(buildApiUrl('/api/v1/me/profile'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      let user = null;
      if (userResponse.ok) {
        const userData = await userResponse.json();
        user = userData.data;
      }

      // 获取用户的所有提示词数据
      const response = await fetch(buildApiUrl('/api/v1/me/prompts?limit=10000'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('获取数据失败');
      }

      const promptsData = await response.json();
      
      console.log('Prompts data:', promptsData); // 调试信息
      console.log('User data:', user); // 调试信息
      
      // 创建备份数据结构
      const backupData = {
        backupInfo: {
          backupId: `manual_download_${Date.now()}`,
          timestamp: Date.now(),
          backupType: 'manual_download',
          version: '1.0',
          generatedBy: 'PromptMaster v0.8',
        },
        userData: {
          email: user?.email,
          createdAt: user?.createdAt,
          totalPrompts: promptsData.data?.total || 0,
        },
        prompts: promptsData.data?.prompts || [],
        statistics: settings?.includeStatistics ? {
          totalPrompts: promptsData.data?.total || 0,
          lastExportTime: Date.now(),
        } : undefined,
      };

      // 创建下载文件
      let content: string;
      let mimeType: string;
      let extension: string;

      if (format === 'csv') {
        // 生成CSV格式
        const csvHeader = [
          '提示词标题',
          '内容',
          '描述',
          '分类',
          '标签',
          '使用次数',
          '查看次数',
          '是否收藏',
          '创建时间',
          '更新时间'
        ].join(',');
        
        const prompts = promptsData.data?.prompts || promptsData.prompts || [];
        console.log('CSV prompts count:', prompts.length); // 调试信息
        
        if (prompts.length === 0) {
          throw new Error('没有可导出的提示词数据');
        }
        
        console.log('Starting CSV generation...'); // 调试信息
        
        const csvRows = prompts.map((prompt: any, index: number) => {
          try {
            // 处理标签数组
            const tagsStr = Array.isArray(prompt.tags) ? prompt.tags.join(';') : (prompt.tags || '');
            
            console.log(`Processing prompt ${index + 1}/${prompts.length}:`, prompt.title); // 调试信息
            
            const row = [
              `"${(prompt.title || '').replace(/"/g, '""')}"`,
              `"${(prompt.content || '').replace(/"/g, '""')}"`,
              `"${(prompt.description || '').replace(/"/g, '""')}"`,
              `"${(prompt.category || '').replace(/"/g, '""')}"`,
              `"${tagsStr.replace(/"/g, '""')}"`,
              prompt.usageCount || 0,
              prompt.viewCount || 0,
              prompt.isFavorite ? '是' : '否',
              `"${new Date(prompt.createdAt).toLocaleString('zh-CN')}"`,
              `"${new Date(prompt.updatedAt).toLocaleString('zh-CN')}"` 
            ].join(',');
            
            return row;
          } catch (rowError: any) {
            console.error(`Error processing prompt ${index}:`, rowError, prompt);
            throw new Error(`处理第${index + 1}条提示词时出错: ${rowError.message || String(rowError)}`);
          }
        });
        
        console.log('CSV rows generated successfully, count:', csvRows.length); // 调试信息
        
        try {
          content = [csvHeader, ...csvRows].join('\n');
          console.log('CSV content joined successfully, length:', content.length); // 调试信息
        } catch (joinError) {
          console.error('Error joining CSV content:', joinError);
          throw new Error(`CSV内容拼接失败: ${joinError}`);
        }
        mimeType = 'text/csv;charset=utf-8';
        extension = 'csv';
      } else {
        // 生成JSON格式
        content = JSON.stringify(backupData, null, 2);
        mimeType = 'application/json';
        extension = 'json';
      }

      const blob = new Blob(['\ufeff' + content], { type: mimeType }); // 添加BOM以支持中文
      const filename = `promptmaster_backup_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.${extension}`;
      
      console.log('Downloading file:', filename); // 调试信息
      console.log('Blob size:', blob.size); // 调试信息
      console.log('Content preview:', content.substring(0, 200)); // 调试信息
      
      // 尝试使用现代下载API（仅在用户主动选择时使用）
      if ('showSaveFilePicker' in window && false) { // 暂时禁用，使用传统方法
        try {
          // 使用文件系统访问API（Chrome 86+）
          const fileHandle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: format === 'csv' ? 'CSV files' : 'JSON files',
              accept: {
                [mimeType]: [`.${extension}`]
              }
            }]
          });
          
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          
          setSuccess(`${format.toUpperCase()}备份文件已保存！文件名: ${filename}`);
          return;
        } catch (saveError) {
          console.log('Modern save API failed, falling back to traditional method:', saveError);
        }
      }
      
      // 回退到传统方法
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none'; // 隐藏链接
      
      document.body.appendChild(link);
      
      console.log('About to trigger download...'); // 调试信息
      
      // 触发下载 - 使用最简单的方法
      try {
        link.click();
        console.log('Download triggered successfully'); // 调试信息
        
      } catch (error) {
        console.error('All download methods failed:', error);
        
        // 最后的备用方案：创建一个用户可见的下载链接
        const visibleLink = document.createElement('a');
        visibleLink.href = url;
        visibleLink.download = filename;
        visibleLink.textContent = `点击下载 ${filename}`;
        visibleLink.style.cssText = 'display: inline-block; padding: 10px 15px; background: #4CAF50; color: white; text-decoration: none; border-radius: 4px; margin: 10px 0; font-weight: bold;';
        visibleLink.addEventListener('click', () => {
          console.log('Manual download link clicked');
        });
        
        // 显示手动下载链接
        const container = document.createElement('div');
        container.style.cssText = 'background: #e8f5e8; border: 1px solid #4CAF50; padding: 15px; border-radius: 6px; margin: 10px 0;';
        container.innerHTML = '<strong>自动下载被浏览器阻止，请点击下方按钮手动下载：</strong><br/><br/>';
        container.appendChild(visibleLink);
        
        // 插入到页面中
        const targetElement = document.querySelector('.space-y-6') || document.body;
        targetElement.appendChild(container);
        
        setError('');
        setSuccess('下载链接已生成，请点击下方绿色按钮下载文件');
        
        // 5秒后自动清理
        setTimeout(() => {
          if (targetElement.contains(container)) {
            targetElement.removeChild(container);
          }
        }, 30000); // 30秒后清理
        
        return;
      }
      
      // 清理
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        URL.revokeObjectURL(url);
      }, 1000);

      setSuccess(`${format.toUpperCase()}备份文件已生成并下载！文件名: ${filename}`);
    } catch (error: any) {
      setError(error.message || '下载备份失败');
    } finally {
      setTriggering(false);
    }
  };

  // 格式化日期
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('zh-CN');
  };

  // 格式化文件大小
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${Math.round(bytes / (1024 * 1024))} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">加载中...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 页面标题和返回按钮 */}
      <div className="flex items-center space-x-4">
        {onNavigate && (
          <button
            onClick={() => onNavigate('profile')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            返回个人中心
          </button>
        )}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">备份设置</h1>
          <p className="mt-1 text-sm text-gray-500">
            管理您的数据备份设置，确保数据安全
          </p>
        </div>
      </div>

      {/* 错误和成功提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* 备份统计 */}
      {stats && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">备份概览</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-blue-900">总备份数</p>
                  <p className="text-2xl font-semibold text-blue-600">{stats.totalBackups}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-green-900">成功备份</p>
                  <p className="text-2xl font-semibold text-green-600">{stats.successfulBackups}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-red-900">失败备份</p>
                  <p className="text-2xl font-semibold text-red-600">{stats.failedBackups}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-purple-900">总大小</p>
                  <p className="text-2xl font-semibold text-purple-600">{formatSize(stats.totalSize)}</p>
                </div>
              </div>
            </div>
          </div>

          {stats.lastBackup && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900 mb-2">最近备份</h3>
              <div className="text-sm text-gray-600">
                <p>时间: {formatDate(stats.lastBackup.timestamp)}</p>
                <p>状态: <span className={`font-medium ${stats.lastBackup.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                  {stats.lastBackup.status === 'success' ? '✅ 成功' : '❌ 失败'}
                </span></p>
                <p>大小: {formatSize(stats.lastBackup.size)}</p>
                <p>提示词数量: {stats.lastBackup.promptCount}</p>
              </div>
            </div>
          )}

          {stats.nextBackupDue && stats.backupEnabled && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-sm font-medium text-blue-900 mb-2">下次自动备份</h3>
              <p className="text-sm text-blue-700">
                {formatDate(stats.nextBackupDue)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* 备份设置 */}
      {settings && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">备份设置</h2>
          
          <div className="space-y-6">
            {/* 启用备份 */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-900">启用自动备份</h3>
                <p className="text-sm text-gray-500">定期自动备份您的提示词数据到邮箱</p>
              </div>
              <button
                type="button"
                className={`${
                  settings.enabled ? 'bg-blue-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                onClick={() => updateSettings({ enabled: !settings.enabled })}
                disabled={saving}
              >
                <span
                  className={`${
                    settings.enabled ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>

            {/* 备份频率 */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                备份频率
              </label>
              <select
                value={settings.frequency}
                onChange={(e) => updateSettings({ frequency: e.target.value as 'daily' | 'weekly' | 'monthly' })}
                disabled={saving || !settings.enabled}
                className="block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="daily">每日</option>
                <option value="weekly">每周</option>
                <option value="monthly">每月</option>
              </select>
            </div>

            {/* 备份邮箱 */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                备份邮箱地址
              </label>
              <input
                type="email"
                value={settings.emailAddress}
                onChange={(e) => updateSettings({ emailAddress: e.target.value })}
                disabled={saving}
                className="block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                placeholder="输入接收备份文件的邮箱地址"
              />
              <p className="mt-1 text-sm text-gray-500">
                备份文件将发送到此邮箱地址
              </p>
            </div>

            {/* 高级选项 */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900">高级选项</h3>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">包含使用统计</h4>
                  <p className="text-sm text-gray-500">在备份中包含提示词使用统计数据</p>
                </div>
                <button
                  type="button"
                  className={`${
                    settings.includeStatistics ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                  onClick={() => updateSettings({ includeStatistics: !settings.includeStatistics })}
                  disabled={saving}
                >
                  <span
                    className={`${
                      settings.includeStatistics ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">启用压缩</h4>
                  <p className="text-sm text-gray-500">压缩备份文件以减小邮件大小</p>
                </div>
                <button
                  type="button"
                  className={`${
                    settings.compressionEnabled ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                  onClick={() => updateSettings({ compressionEnabled: !settings.compressionEnabled })}
                  disabled={saving}
                >
                  <span
                    className={`${
                      settings.compressionEnabled ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">文件格式</h4>
                <p className="text-sm text-gray-500 mb-3">选择邮件备份的文件格式</p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => updateSettings({ fileFormat: 'csv' })}
                    disabled={saving}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                      settings.fileFormat === 'csv'
                        ? 'bg-green-50 border-green-200 text-green-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      CSV (推荐)
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Excel友好，易于查看</div>
                  </button>
                  <button
                    onClick={() => updateSettings({ fileFormat: 'json' })}
                    disabled={saving}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                      settings.fileFormat === 'json'
                        ? 'bg-blue-50 border-blue-200 text-blue-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                      </svg>
                      JSON
                    </div>
                    <div className="text-xs text-gray-500 mt-1">结构化数据，开发友好</div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 数据备份与下载 */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">数据备份与下载</h2>
        <p className="text-sm text-gray-500 mb-6">
          选择备份格式，然后下载到本地或发送到邮箱
        </p>
        
        {/* 格式选择器 */}
        <div className="mb-6">
          <label className="text-sm font-medium text-gray-700 mb-3 block">选择备份格式</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className={`relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none ${
              backupFormat === 'csv' 
                ? 'border-green-300 bg-green-50 ring-2 ring-green-200' 
                : 'border-gray-300 bg-white hover:bg-gray-50'
            }`}>
              <input
                type="radio"
                name="backupFormat"
                value="csv"
                checked={backupFormat === 'csv'}
                onChange={(e) => setBackupFormat(e.target.value as 'json' | 'csv')}
                className="sr-only"
              />
              <span className="flex flex-1">
                <span className="flex flex-col">
                  <span className="block text-sm font-medium text-gray-900 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    CSV格式
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      推荐
                    </span>
                  </span>
                  <span className="mt-1 text-xs text-gray-500">
                    Excel友好，表格形式，易于查看和编辑
                  </span>
                </span>
              </span>
            </label>
            
            <label className={`relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none ${
              backupFormat === 'json' 
                ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-200' 
                : 'border-gray-300 bg-white hover:bg-gray-50'
            }`}>
              <input
                type="radio"
                name="backupFormat"
                value="json"
                checked={backupFormat === 'json'}
                onChange={(e) => setBackupFormat(e.target.value as 'json' | 'csv')}
                className="sr-only"
              />
              <span className="flex flex-1">
                <span className="flex flex-col">
                  <span className="block text-sm font-medium text-gray-900 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                    </svg>
                    JSON格式
                  </span>
                  <span className="mt-1 text-xs text-gray-500">
                    结构化数据，开发友好，保留完整信息
                  </span>
                </span>
              </span>
            </label>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => downloadBackup(backupFormat)}
            disabled={triggering}
            className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {triggering ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                生成中...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                下载到本地 ({backupFormat.toUpperCase()})
              </>
            )}
          </button>
          
          <button
            onClick={() => triggerManualBackupWithFormat(backupFormat)}
            disabled={triggering}
            className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {triggering ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                发送中...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                发送到邮箱 ({backupFormat.toUpperCase()})
              </>
            )}
          </button>
        </div>
        
        <div className="mt-4 p-4 bg-blue-50 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                💡 <strong>CSV格式</strong>：可直接在Excel中打开编辑，查看表格数据<br/>
                📋 <strong>JSON格式</strong>：保留完整数据结构，便于程序处理和导入<br/>
                📧 <strong>邮件发送</strong>：文件将作为附件发送到您的邮箱 {user?.email}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 备份历史 */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">备份历史</h2>
        {history.length === 0 ? (
          <p className="text-sm text-gray-500">暂无备份记录</p>
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    时间
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    类型
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    大小
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    邮件
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {history.map((entry) => (
                  <tr key={entry.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(entry.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        entry.backupType === 'manual' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {entry.backupType === 'manual' ? '手动' : '自动'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        entry.status === 'success' 
                          ? 'bg-green-100 text-green-800'
                          : entry.status === 'failed'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {entry.status === 'success' ? '✅ 成功' : 
                         entry.status === 'failed' ? '❌ 失败' : '⏳ 进行中'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatSize(entry.size)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {entry.emailSent ? '✅ 已发送' : '❌ 未发送'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}; 